/**
 * Реестр разделов и подразделов админ-панели для гранулярного доступа.
 *
 * Суперадмин может выдать обычному админу доступ и к разделу целиком
 * («только Дашборд»), и к отдельным его подразделам — вкладкам внутри
 * страницы («eSIM → только Шедулеры»). Список ключей хранится в
 * `admin.allowed_sections` (см. backend `api/admin/sections.py`).
 *
 * Формат ключей:
 *  - раздел     — `'esims'`
 *  - подраздел  — `'esims:schedulers'` (`<section>:<subsection>`)
 *
 * Соглашения:
 *  - `allowed_sections === null` → доступ ко ВСЕМУ (default + обратная
 *    совместимость);
 *  - раздел в списке БЕЗ единого своего подраздела → раздел целиком,
 *    включая все вкладки (в том числе будущие);
 *  - как только для раздела выбран хотя бы один подраздел — доступны
 *    только перечисленные;
 *  - `role === 'superadmin'` → всегда полный доступ;
 *  - раздел `admins` виден только суперадмину и НЕ входит в выдаваемый
 *    список.
 *
 * Ключи здесь ОБЯЗАНЫ совпадать с backend `SECTIONS` и с пунктами меню в
 * `AppLayout.vue`. Форма выдачи прав (`AdminsView.vue`) рисует дерево не
 * отсюда, а из `GET /admins/sections` — чтобы backend оставался
 * единственным источником истины.
 */
import type { AdminOut } from '@/api/types'

export const KEY_SEPARATOR = ':'

/**
 * Группы бокового меню. Пункты внутри группы сворачиваются вместе, а
 * состояние «свёрнута/раскрыта» запоминается в localStorage
 * (см. `AppLayout.vue`).
 *
 * Зачем группы: разделов больше двадцати, и после появления второго
 * продукта (одноразовые номера) плоский список перестал читаться.
 * Ключи и подписи продублированы в backend `api/admin/sections.py`,
 * оттуда же их берёт форма выдачи прав.
 */
export type SectionGroup = 'sms' | 'esim' | 'general'

export const GROUP_ORDER: SectionGroup[] = ['sms', 'esim', 'general']

export const GROUP_LABEL: Record<SectionGroup, string> = {
  sms: 'SMS',
  esim: 'eSIM',
  general: 'Общие',
}

export interface SubsectionDef {
  /** Короткий ключ внутри раздела (`'schedulers'`). */
  key: string
  /** Человекочитаемая подпись (для UI прав). */
  label: string
  /**
   * Не вкладка, а отдельное право (`payments:refund`): полный доступ и
   * «раздел целиком» его не дают, а сам ключ не сужает раздел.
   */
  explicit?: boolean
}

export interface SectionDef {
  /** Ключ раздела (совпадает с backend). */
  key: string
  /** Базовый путь роута (для резолва текущего раздела по URL). */
  path: string
  /** Человекочитаемая подпись (для UI прав). */
  label: string
  /** Только для суперадмина (раздел «Админы»). */
  superadminOnly?: boolean
  /** Группа бокового меню. По умолчанию `'general'`. */
  group?: SectionGroup
  /** Подразделы = вкладки/блоки внутри страницы раздела. */
  children?: SubsectionDef[]
}

/**
 * Полный список разделов в порядке меню. `admins` помечен
 * `superadminOnly` — его нельзя выдать обычному админу.
 */
export const SECTIONS: SectionDef[] = [
  {
    key: 'dashboard',
    path: '/',
    label: 'Дашборд',
    children: [
      { key: 'revenue', label: 'Выручка' },
      { key: 'clients', label: 'Клиенты' },
      { key: 'esim', label: 'eSIM' },
      { key: 'traffic', label: 'Трафик' },
      { key: 'devices', label: 'Устройства' },
      { key: 'sms', label: 'SMS выручка' },
      { key: 'charts', label: 'Мои графики' },
      { key: 'layout', label: 'Настройка дашборда' },
    ],
  },
  {
    key: 'users',
    path: '/users',
    label: 'Клиенты',
    children: [
      { key: 'payments', label: 'Карточка: Платежи' },
      { key: 'esims', label: 'Карточка: eSIM' },
      { key: 'esim-issue', label: 'Карточка: Выпуск eSIM' },
      { key: 'discounts', label: 'Карточка: Промокоды' },
      { key: 'balance', label: 'Карточка: Баланс' },
      { key: 'referral', label: 'Карточка: Реф. баланс' },
      { key: 'referrals', label: 'Карточка: Рефералы' },
      { key: 'autopay', label: 'Карточка: Автоплатёж' },
    ],
  },
  { key: 'acquirers', path: '/acquirers', label: 'Эквайринги' },
  {
    key: 'providers',
    path: '/providers',
    label: 'eSIM провайдеры',
    group: 'esim',
    children: [
      { key: 'providers', label: 'Провайдеры' },
      { key: 'schedulers', label: 'Шедулеры' },
    ],
  },
  {
    key: 'plans',
    path: '/plans',
    label: 'Тарифы',
    group: 'esim',
    children: [
      { key: 'plans', label: 'Тарифы' },
      { key: 'price-history', label: 'История цен' },
      { key: 'expenses', label: 'Расходы' },
      { key: 'scripts', label: 'Скрипты' },
      { key: 'compare', label: 'Сравнение' },
      { key: 'topup', label: 'Продление' },
      { key: 'frontend', label: 'Фронтенд' },
    ],
  },
  // Подразделов нет: раздел устроен как список площадок, а внутри
  // каждой — свои кабинеты. Права выдаются на раздел целиком.
  { key: 'marketplaces', path: '/marketplaces', label: 'Маркетплейсы', group: 'esim' },
  { key: 'discounts', path: '/discounts', label: 'Промокоды' },
  {
    key: 'payments',
    path: '/payments',
    label: 'Платежи',
    children: [
      { key: 'payments', label: 'Платежи' },
      { key: 'schedulers', label: 'Шедулеры' },
      { key: 'scripts', label: 'Скрипты' },
      { key: 'refund', label: 'Возврат денег', explicit: true },
    ],
  },
  {
    key: 'finance',
    path: '/finance',
    label: 'Финансы',
    children: [
      { key: 'report', label: 'Отчёт' },
      { key: 'partners', label: 'Партнёры' },
      { key: 'expenses', label: 'Расходы' },
      { key: 'integrations', label: 'Интеграции' },
    ],
  },
  // Внешние партнёры с реферальными ссылками. Не путать с «Финансы →
  // Партнёры» (доли совладельцев): права на анкеты и на выплаты живыми
  // деньгами выдаются разным людям, поэтому это отдельный раздел.
  {
    key: 'partner-program',
    path: '/partner-program',
    label: 'Партнёрская программа',
    children: [
      { key: 'partners', label: 'Партнёры' },
      { key: 'payouts', label: 'Выплаты' },
    ],
  },
  {
    // Подразделы = ключи отчётов в реестре
    // api/admin/services/reports/__init__.py. Исключения — «Снимки» и
    // «Лог посещений»: это не отчёты, а отдельные роутеры `/snapshots` и
    // `/visit-log`, права на которые проверяются правилами в
    // api/admin/sections.py::SUBSECTION_ROUTES.
    key: 'reports',
    path: '/reports',
    label: 'Отчёты',
    children: [
      { key: 'esim', label: 'eSIM' },
      { key: 'plans', label: 'Тарифы' },
      { key: 'payments', label: 'Платежи' },
      { key: 'discounts', label: 'Промокоды' },
      { key: 'snapshots', label: 'Снимки' },
      { key: 'visit-log', label: 'Лог посещений' },
    ],
  },
  {
    key: 'esims',
    path: '/esims',
    label: 'eSIM',
    group: 'esim',
    children: [
      { key: 'esims', label: 'eSIM' },
      { key: 'scripts', label: 'Скрипты' },
      { key: 'schedulers', label: 'Шедулеры' },
      { key: 'expiry', label: 'Оповещения' },
      { key: 'docs', label: 'Документация' },
    ],
  },
  { key: 'api-client', path: '/api-client', label: 'Клиентский API' },
  {
    key: 'smtp',
    path: '/smtp',
    label: 'SMTP',
    children: [
      { key: 'servers', label: 'Серверы' },
      { key: 'templates', label: 'Шаблоны' },
    ],
  },
  // ИСХОДЯЩИЕ SMS нашим клиентам (коды подтверждения телефона,
  // уведомления). К группе «SMS» НЕ относится: там продукт про приём
  // ЧУЖИХ кодов. Отсюда и переименование — иначе два разных домена
  // назывались бы одинаково.
  {
    key: 'sms',
    path: '/sms',
    label: 'SMS-уведомления',
    children: [
      { key: 'providers', label: 'Провайдеры' },
      { key: 'templates', label: 'Шаблоны' },
      { key: 'countries', label: 'Страны' },
    ],
  },
  {
    key: 'telegram',
    path: '/telegram',
    label: 'Telegram',
    children: [
      { key: 'accounts', label: 'Аккаунты' },
      { key: 'alerts', label: 'Алёрты' },
    ],
  },
  {
    key: 'push',
    path: '/push',
    label: 'Push',
    children: [
      { key: 'overview', label: 'Обзор' },
      { key: 'events', label: 'События' },
      { key: 'send', label: 'Отправка' },
      { key: 'devices', label: 'Устройства' },
      { key: 'history', label: 'История' },
    ],
  },
  { key: 'cache', path: '/cache', label: 'Кэш' },
  { key: 'test', path: '/test', label: 'Тест' },
  {
    key: 'broadcasts',
    path: '/broadcasts',
    label: 'Рассылка',
    children: [
      { key: 'scripts', label: 'Рассылка' },
      { key: 'templates', label: 'Шаблоны' },
      { key: 'audience', label: 'Аудитория' },
      { key: 'history', label: 'История' },
    ],
  },
  {
    key: 'support',
    path: '/support',
    label: 'Поддержка',
    children: [
      { key: 'chats', label: 'Диалоги' },
      { key: 'errors', label: 'Аудит ошибок' },
      { key: 'test', label: 'Тест' },
      { key: 's3', label: 'S3' },
      { key: 'docs', label: 'Документация' },
    ],
  },
  {
    key: 'settings',
    path: '/settings',
    label: 'Настройки',
    children: [
      { key: 'general', label: 'Общие настройки' },
      { key: 'currencies', label: 'Курсы валют' },
      { key: 'prices', label: 'Цены за действия' },
    ],
  },
  { key: 'admins', path: '/admins', label: 'Админы', superadminOnly: true },
  { key: 'audit', path: '/audit', label: 'Аудит' },
  // ---- Группа «SMS»: одноразовые номера для ПРИЁМА чужих кодов ----
  {
    key: 'sms-providers',
    path: '/sms-providers',
    label: 'SMS провайдеры',
    group: 'sms',
    children: [
      { key: 'providers', label: 'Агрегаторы' },
      { key: 'activations', label: 'Активации' },
      { key: 'markup', label: 'Наценка' },
    ],
  },
  { key: 'sms-countries', path: '/sms-countries', label: 'SMS страны', group: 'sms' },
  { key: 'sms-services', path: '/sms-services', label: 'SMS сервисы', group: 'sms' },
  // Реестр прокси. Инфраструктура общего назначения, но лежит в группе
  // «SMS»: сегодня его единственный потребитель — кабинеты одноразовых
  // номеров (домен Grizzly SMS в реестре РКН). Порядок и группа
  // продублированы в backend `api/admin/sections.py`.
  { key: 'proxies', path: '/proxies', label: 'Прокси', group: 'sms' },
]

/** Группа раздела с дефолтом `'general'`. */
export function groupOf(section: SectionDef): SectionGroup {
  return section.group ?? 'general'
}

/** Разделы, которые суперадмин может выдать (без `superadminOnly`). */
export const GRANTABLE_SECTIONS: SectionDef[] = SECTIONS.filter(
  (s) => !s.superadminOnly,
)

/** `('esims', 'scripts') → 'esims:scripts'`. */
export function subKey(sectionKey: string, subsectionKey: string): string {
  return `${sectionKey}${KEY_SEPARATOR}${subsectionKey}`
}

/** `'esims:scripts' → ['esims', 'scripts']`; `'esims' → ['esims', null]`. */
export function splitKey(key: string): [string, string | null] {
  const at = key.indexOf(KEY_SEPARATOR)
  return at === -1 ? [key, null] : [key.slice(0, at), key.slice(at + 1)]
}

/**
 * Определить ключ раздела по пути роута. `/` = dashboard; для остальных
 * берём раздел с самым длинным совпадающим префиксом (чтобы `/users/42`
 * и `/support/abc` резолвились в `users` / `support`).
 */
export function sectionKeyForPath(path: string): string | null {
  if (path === '/') return 'dashboard'
  let best: SectionDef | null = null
  for (const s of SECTIONS) {
    if (s.path === '/') continue
    if (path === s.path || path.startsWith(s.path + '/') || path.startsWith(s.path)) {
      if (!best || s.path.length > best.path.length) best = s
    }
  }
  return best?.key ?? null
}

/** Является ли админ суперадмином. */
export function isSuperadmin(admin: AdminOut | null | undefined): boolean {
  return admin?.role === 'superadmin'
}

/**
 * Есть ли у админа доступ к разделу (хотя бы к одному его подразделу).
 * Суперадмин — всегда; `admins` — только суперадмин;
 * `allowed_sections === null` — полный доступ.
 */
export function canAccessSection(
  admin: AdminOut | null | undefined,
  key: string,
): boolean {
  if (!admin) return false
  const def = SECTIONS.find((s) => s.key === key)
  if (def?.superadminOnly) return isSuperadmin(admin)
  if (isSuperadmin(admin)) return true
  const allowed = admin.allowed_sections
  if (allowed == null) return true
  return allowed.includes(key)
}

/** Полные ключи явных подразделов (`'payments:refund'`). */
export const EXPLICIT_SUBSECTION_KEYS: ReadonlySet<string> = new Set(
  SECTIONS.flatMap((s) =>
    (s.children ?? []).filter((c) => c.explicit).map((c) => subKey(s.key, c.key)),
  ),
)

/**
 * Есть ли у админа доступ к подразделу (вкладке) раздела.
 *
 * Раздел, выданный целиком (в списке нет ни одного его подраздела), даёт
 * доступ ко всем вкладкам — включая те, что появятся в будущих релизах.
 * Явный подраздел (`explicit`) — наоборот, только если ключ в списке.
 */
export function canAccessSubsection(
  admin: AdminOut | null | undefined,
  sectionKey: string,
  subsectionKey: string,
): boolean {
  if (!canAccessSection(admin, sectionKey)) return false
  if (isSuperadmin(admin)) return true
  const allowed = admin?.allowed_sections
  const full = subKey(sectionKey, subsectionKey)
  if (EXPLICIT_SUBSECTION_KEYS.has(full)) return allowed?.includes(full) ?? false
  if (allowed == null) return true
  const prefix = sectionKey + KEY_SEPARATOR
  if (!allowed.some((k) => k.startsWith(prefix) && !EXPLICIT_SUBSECTION_KEYS.has(k))) {
    return true
  }
  return allowed.includes(full)
}

/**
 * Куда отправить админа «по умолчанию»: после логина и вместо раздела,
 * который ему не выдан.
 *
 * Обычно это дашборд, но его тоже можно не выдать — тогда берём первый
 * доступный пункт меню, иначе админ упирался бы в пустую страницу. Если
 * не доступно вообще ничего, всё равно возвращаем `/`: там показывается
 * заглушка «нет доступа ни к одному разделу».
 */
export function landingPath(admin: AdminOut | null | undefined): string {
  for (const s of SECTIONS) {
    if (canAccessSection(admin, s.key)) return s.path
  }
  return '/'
}